import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const defaultOptions = {
  root: null,
  rootMargin: '0px',
  threshold: 1,
};

export const useIsElementVisible = <T extends HTMLElement = HTMLElement>(
  options?: IntersectionObserverInit,
  initialVisibleValue: boolean = false,
): [observableRef: MutableRefObject<null | T>, isVisible: boolean] => {
  const mergedOptions = useMemo<IntersectionObserverInit>(
    () => ({
      ...defaultOptions,
      ...options,
    }),
    [options],
  );

  const observableRef = useRef<null | T>(null);
  const [isVisible, setIsVisible] = useState(Boolean(initialVisibleValue));

  const callbackFunction = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry) {
        setIsVisible(entry.isIntersecting);
      }
    },
    [],
  );

  useEffect(() => {
    const currentContainerRef = observableRef.current;

    const observer = new IntersectionObserver(callbackFunction, mergedOptions);
    if (currentContainerRef) observer.observe(currentContainerRef);

    return () => {
      if (currentContainerRef) observer.unobserve(currentContainerRef);
    };
  }, [callbackFunction, mergedOptions, observableRef]);

  return [observableRef, isVisible];
};
